// Create or edit an Experiments v2 draft. Only drafts can be edited; the
// server refuses the rest, and EditorBody says so before the admin tries.
import React, { useEffect, useState } from 'react';
import { Alert, Card, Spin, Typography, message } from 'antd';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { adminApi } from '@/services/api';
import type { ExpV2Kind, ExpV2Layer, ExpV2Status, ExpV2UserList } from '@/types/experimentsV2';
import { apiErrorMessage } from '@/utils/apiError';
import { EditorBody } from './EditorBody';
import {
  draftProblems,
  emptyDraft,
  isTestKey,
  fromTest,
  toCreateBody,
  toUpdateBody,
  type DraftForm,
} from './draftForm';

export const EditorPage: React.FC = () => {
  const navigate = useNavigate();
  const { key: rawKey } = useParams<{ key: string }>();
  const badKey = rawKey !== undefined && !isTestKey(rawKey);
  const key = badKey ? undefined : rawKey;
  const [params] = useSearchParams();
  const mode = key ? 'edit' : 'create';
  const kind: ExpV2Kind = params.get('kind') === 'flag' ? 'flag' : 'experiment';

  const [form, setForm] = useState<DraftForm>(() => emptyDraft(kind));
  const [status, setStatus] = useState<ExpV2Status>('draft');
  const [layers, setLayers] = useState<ExpV2Layer[]>([]);
  const [lists, setLists] = useState<ExpV2UserList[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let live = true;
    if (badKey) {
      setLoadError(`${rawKey} is not a test key`);
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadError(null);
    Promise.all([
      adminApi.experimentsV2.listLayers(),
      adminApi.experimentsV2.listLists(),
      key ? adminApi.experimentsV2.get(key) : Promise.resolve(null),
    ])
      .then(([l, u, t]) => {
        if (!live) return;
        setLayers(l.data);
        setLists(u.data);
        if (t) {
          setForm(fromTest(t.data));
          setStatus(t.data.status);
        } else {
          setForm(emptyDraft(kind));
          setStatus('draft');
        }
      })
      .catch((e) => live && setLoadError(apiErrorMessage(e, 'Could not load the editor')))
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
  }, [key, kind, badKey, rawKey]);

  const save = async () => {
    if (draftProblems(form).length > 0) return;
    setSaving(true);
    setServerError(null);
    try {
      if (mode === 'create') {
        await adminApi.experimentsV2.create(toCreateBody(form));
      } else {
        await adminApi.experimentsV2.update(form.key, toUpdateBody(form));
      }
      message.success(mode === 'create' ? `Draft ${form.key} saved` : `${form.key} saved`);
      navigate(`/experiments-v2/${form.key}`);
    } catch (e) {
      // The server's refusal is shown word for word above the form.
      setServerError(apiErrorMessage(e, 'Save failed'));
    } finally {
      setSaving(false);
    }
  };

  const title =
    mode === 'create'
      ? kind === 'flag'
        ? 'New rollout flag'
        : 'New A/B test'
      : `Edit ${key ?? ''}`;

  return (
    <Card>
      <Typography.Title level={4}>{title}</Typography.Title>
      {loadError && <Alert type="error" showIcon message={loadError} />}
      {loading ? (
        <Spin />
      ) : (
        !loadError && (
          <EditorBody
            mode={mode}
            status={status}
            form={form}
            onChange={setForm}
            layers={layers}
            lists={lists}
            serverError={serverError}
            saving={saving}
            onSave={() => void save()}
            onCancel={() => navigate(key ? `/experiments-v2/${key}` : '/experiments-v2')}
          />
        )
      )}
    </Card>
  );
};
